import { useServices } from "@/services/hooks/useServices";
import { AuthProvider } from "@/types/common";
import { AssignedLabel, ILabel } from "@/types/label.types";
import {
  AwsApiPayload,
  AwsSmtpPayload,
  AzureSmtpPayload,
  B2BWarmupSettings,
  MailboxType,
  SenderMailboxStats,
  SendGridApiPayload,
  SendGridSmtpPayload,
  TargetAudience,
  UpdateMailboxSettings,
  VerifySendGridApiPayload,
} from "@/types/mailbox.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
export enum WarmupSpeed {
  SLOW = "slow",
  MEDIUM = "medium",
  FAST = "fast",
}

export const useMailboxes = (
  page: number,
  limit: number,
  selectedLabels?: string[],
  quickFilterText?: string,
  selectedEspTypes?: string[],
  selectedWarmupStatus?: boolean | undefined,
  selectedPlanMetaData?: string[] | null,
  selectedConnectionStatus?: boolean | undefined,
  minInboxPercentage?: number | undefined,
  maxInboxPercentage?: number | undefined
) => {
  const { mailboxService } = useServices();

  return useQuery({
    queryKey: ["mailboxes", { page, limit, selectedLabels, quickFilterText, selectedEspTypes, selectedWarmupStatus, selectedPlanMetaData, selectedConnectionStatus, minInboxPercentage, maxInboxPercentage }],
    queryFn: () => {
      const params = {
        page,
        limit,
        selectedLabels: selectedLabels || [],
        quickFilterText: quickFilterText || "",
        selectedEspTypes: selectedEspTypes || [],
        selectedWarmupStatus: selectedWarmupStatus === true ? true : selectedWarmupStatus === false ? false : undefined,
        selectedPlanMetaData: selectedPlanMetaData || [],
        selectedConnectionStatus: selectedConnectionStatus === true ? true : selectedConnectionStatus === false ? false : undefined,
        minInboxPercentage,
        maxInboxPercentage,
      };
            
      return mailboxService.getMailboxes(params);
    },
  });
};

export const useMailboxesbyId = (id: string) => {
  const { mailboxService } = useServices();

  return useQuery({
    queryKey: ["mailboxes", { id }],
    queryFn: () => mailboxService.getMailboxesbyId(id),
  });
};

export const useSearchMailboxes = (email?: string, espType?: AuthProvider) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["mailbox", "search", email, espType],
    queryFn: () => mailboxService.searchMailboxes(email, espType),
  });
};

export const useMailboxesbyEmail = (email: string) => {
  const { mailboxService } = useServices();

  return useQuery({
    queryKey: ["mailboxes", { email }],
    queryFn: () => mailboxService.getMailboxesbyEmail(email),
  });
};

export const useDownloadFailedHeaderssenders = () => {
  const { mailboxService } = useServices();
  return useMutation({
    mutationFn: ({ date, email }: { date: string; email: string }) =>
      mailboxService.downloadFailedHeader(date, email),
  });
};

export const useMailboxStats = () => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["mailboxStats"],
    queryFn: () => mailboxService.getMailboxStats(),
  });
};
export const useCreateMailbox = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mailboxData: Partial<MailboxType>) =>
      mailboxService.createMailbox(mailboxData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useUpdateMailbox = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      mailboxData,
    }: {
      id: string;
      mailboxData: Partial<MailboxType>;
    }) => mailboxService.updateMailbox(id, mailboxData),

    onSuccess: (data, variables) => {
      toast.success("Mailbox updated successfully");
      queryClient.setQueryData(["mailboxes", { id: variables.id }], data);
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
    onError: () => {
      toast.error("Failed to update mailbox");
    },
  });
};

export const useUpdateMailboxWarmup = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      mailboxData,
    }: {
      id: string;
      mailboxData: Partial<MailboxType>;
    }) => mailboxService.updateMailboxWarmup(id, mailboxData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useBulkUpdateMailboxWarmup = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      updates,
    }: {
      ids: string[];
      updates: Partial<MailboxType>;
    }) => mailboxService.bulkUpdateMailboxWarmup({ ids, updates }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useGetB2BWarmupSettings = (mailboxId: string | null) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["b2bWarmupSettings", mailboxId],
    queryFn: () => mailboxService.getB2BWarmupSettings(mailboxId!),
    enabled: !!mailboxId,
  });
};

export const useSaveB2BWarmupSettings = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mailboxId,
      settings,
    }: {
      mailboxId: string;
      settings: B2BWarmupSettings;
    }) => mailboxService.saveB2BWarmupSettings(mailboxId, settings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["b2bWarmupSettings", variables.mailboxId],
      });
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

/** Enable warmup for one mailbox and store B2B settings (single API call) */
export const useEnableWarmupWithSettings = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mailboxId,
      settings,
    }: {
      mailboxId: string;
      settings: B2BWarmupSettings;
    }) => mailboxService.enableWarmupWithSettings(mailboxId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

/** Enable warmup for many mailboxes and store same B2B settings (single API call) */
export const useBulkEnableWarmupWithSettings = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { ids: string[] } & B2BWarmupSettings) =>
      mailboxService.bulkEnableWarmupWithSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useDeleteMailbox = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mailboxService.deleteMailbox(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useBulkDeleteMailboxes = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => mailboxService.bulkDeleteMailboxes(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useBulkCreateWarmups = () => {
  const { warmupService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => warmupService.createBulkWarmup(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useBulkUpdateMailboxes = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { ids: string[]; updates: Partial<MailboxType> }) =>
      mailboxService.bulkUpdateMailboxes(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useSenderMailboxStats = (
  id: string,
  startDate: string,
  endDate: string,
  espType?: string
): ReturnType<typeof useQuery<SenderMailboxStats>> => {
  const { mailboxService } = useServices();

  return useQuery<SenderMailboxStats>({
    queryKey: ["senderMailboxStats", id, startDate, endDate, espType],
    queryFn: () =>
      mailboxService.getSenderMailboxStats(id, startDate, endDate, espType),
    enabled: !!id,
  });
};

export const useSenderEspTypes = (email: string) => {
  const { mailboxService } = useServices();

  return useQuery<string[]>({
    queryKey: ["senderEspTypes", email],
    queryFn: () => mailboxService.getSenderEspTypes(email),
    enabled: !!email,
  });
};

export const useUpdateWarmupSpeed = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<MailboxType>;
    }) => mailboxService.updateWarmupSpeed(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useAddMailboxLabel = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, labelId }: { id: string; labelId: string }) =>
      mailboxService.addLabel(id, labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

export const useAddAwsApi = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AwsApiPayload) => mailboxService.addAwsApi(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

export const useAddAwsSmtp = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AwsSmtpPayload) => mailboxService.addAwsSmtp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

export const useAddSendGridApi = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendGridApiPayload) =>
      mailboxService.addSendGridApi(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

export const useVerifySendGridApi = () => {
  const { mailboxService } = useServices();
  return useMutation({
    mutationFn: (data: VerifySendGridApiPayload) =>
      mailboxService.verifySendGridApi(data),
  });
};

export const useAddSendGridSmtp = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendGridSmtpPayload) =>
      mailboxService.addSendGridSmtp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

export const useAddAzureSmtp = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AzureSmtpPayload) => mailboxService.addAzureSmtp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useRemoveMailboxLabel = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, labelId }: { id: string; labelId: string }) =>
      mailboxService.removeLabel(id, labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
  });
};

export const useBulkUpdateMailboxLabels = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      labelIds,
      operation,
    }: {
      ids: string[];
      labelIds: string[];
      operation: "add" | "remove";
      pageMetadata?: {
        page: number;
        limit: number;
        selectedLabels: string[];
        quickFilterText?: string;
      };
    }) => mailboxService.bulkUpdateLabels(ids, labelIds, operation),
    onSuccess: (_data, { ids, labelIds, operation, pageMetadata }) => {
      if (!pageMetadata || ids.length > 10) {
        queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
        return;
      }

      // Optimistic update
      const labelsFromCache = queryClient.getQueryData<{ data: ILabel[] }>([
        "labels",
        {
          page: 1,
          limit: 100,
          labelType: "mailbox",
        },
      ]);

      const lookupMap = new Map<string, AssignedLabel>();

      // update lookup map only for add operation
      if (operation === "add") {
        labelsFromCache?.data.forEach((label) => {
          if (labelIds.includes(label._id)) {
            lookupMap.set(label._id, {
              _id: label._id,
              name: label.name,
              color: label.color,
            });
          }
        });
      }

      queryClient.setQueryData(
        ["mailboxes", pageMetadata],
        (oldData: { data: MailboxType[] }) => {
          const updatedData = oldData.data
            .map((mailbox: MailboxType) => {
              if (ids.includes(mailbox._id)) {
                const currentLabelsMap = new Map(
                  // Null check for mailbox.labels
                  (mailbox.labels || []).map((label) => [label._id, label])
                );
                if (operation === "add") {
                  labelIds.forEach((labelId) => {
                    const label = lookupMap.get(labelId);
                    if (label) {
                      currentLabelsMap.set(label._id, label);
                    }
                  });
                } else {
                  // check if updated label is in selected labels then return null
                  if (
                    pageMetadata?.selectedLabels.length > 0 &&
                    pageMetadata?.selectedLabels.some((id) =>
                      labelIds.includes(id)
                    )
                  ) {
                    return null;
                  }

                  // Remove operation
                  labelIds.forEach((labelId) => {
                    currentLabelsMap.delete(labelId);
                  });
                }

                const updatedLabels = Array.from(currentLabelsMap.values());

                return {
                  ...mailbox,
                  labels: updatedLabels,
                };
              }
              return mailbox;
            })
            .filter((mailbox) => mailbox !== null);

          return {
            ...oldData,
            data: updatedData,
          };
        }
      );
    },
  });
};

// ------------------- Mailbox Template Settings -------------------

export const useMailboxTemplateSettings = (
  mailboxId: string,
  enabled: boolean
) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["mailboxTemplateSettings", mailboxId],
    queryFn: () => mailboxService.getMailboxSettings(mailboxId),
    enabled,
  });
};

export const useUpdateMailboxTemplateSettings = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mailboxId,
      settings,
    }: {
      mailboxId: string;
      settings: UpdateMailboxSettings;
    }) => mailboxService.updateMailboxSettings(mailboxId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxTemplateSettings"] });
    },
  });
};

export const useCreateSeedSender = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: string[] | Array<{ email: string; firstName?: string; lastName?: string; espType?: string }>) => {
      // If array of strings, use old method
      if (typeof data[0] === 'string') {
        return mailboxService.createSeedSender(data as string[]);
      }
      // If array of objects, use new B2C Sender method
      return mailboxService.createB2cSenders(data as Array<{ email: string; firstName?: string; lastName?: string; espType?: string }>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
};

export const useGetActiveMailboxes = () => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["activeMailboxes"],
    queryFn: () => mailboxService.getActiveMailboxes(),
  });
};

export const useDistinctEspTypes = () => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["distinctEspTypes"],
    queryFn: () => mailboxService.getDistinctEspTypes(),
  });
};

export const useGetUniqueDomains = (authProvider: AuthProvider) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["uniqueDomains", authProvider],
    queryFn: () => mailboxService.getUniqueDomains(authProvider),
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
};

export const useGetActiveMailboxesDomain = (searchTerm?: string, espType?: AuthProvider) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["activeMailboxesDomain", searchTerm, espType],
    queryFn: () => mailboxService.getActiveMailboxesDomain(searchTerm, espType),
  });
};

export const useGetSeedSenders = () => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["seedSenders"],
    queryFn: () => mailboxService.getSeedSenders(),
  });
};

// ------------------- Warmup Accounts / B2C Senders -------------------

export const useWarmupAccounts = (filters?: {
  page?: number;
  limit?: number;
  labels?: string[];
  subscriptionIds?: string[];
  planIds?: string[];
  connectionStatus?: string;
  minSendingVolume?: number;
  maxSendingVolume?: number;
  minSeedVolume?: number;
  maxSeedVolume?: number;
  minSeedAssign?: number;
  maxSeedAssign?: number;
}) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["warmupAccounts", filters],
    queryFn: () => mailboxService.getWarmupAccounts(filters),
  });
};

export const useWarmupEnabled = () => {
  const { warmupService } = useServices();
  return useQuery({
    queryKey: ["warmupEnabled"],
    queryFn: () => warmupService.getWarmupEnabled(),
  });
};

export const useWarmupAccountToggle = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled, email }: { id: string; enabled: boolean; email: string }) =>
      mailboxService.toggleWarmupAccount(id, enabled, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

export const useBulkWarmupAccountToggle = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, enabled }: { ids: string[]; enabled: boolean }) =>
      mailboxService.bulkToggleWarmupAccounts(ids, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

export const useDeleteWarmupAccount = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mailboxService.deleteWarmupAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

export const useBulkDeleteWarmupAccounts = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => mailboxService.bulkDeleteWarmupAccounts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

// ------------------- B2C Sender CRUD Hooks -------------------

export const useCreateB2cSender = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (senderData: any) => mailboxService.createB2cSender(senderData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

export const useCreateBulkB2cSenders = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (senders: any[]) => mailboxService.createBulkB2cSenders(senders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

export const useB2cSenderById = (id: string, enabled = true) => {
  const { mailboxService } = useServices();
  
  return useQuery({
    queryKey: ["b2cSender", id],
    queryFn: () => mailboxService.getB2cSenderById(id),
    enabled: enabled && !!id,
  });
};

export const useUpdateB2cSender = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) => 
      mailboxService.updateB2cSender(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSender"] });
    },
  });
};

export const useBulkUpdateB2cSenders = () => {
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: any }) => 
      mailboxService.bulkUpdateB2cSenders(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
    },
  });
};

export const useWarmupStats = () => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["warmupStats"],
    queryFn: () => mailboxService.getWarmupStats(),
  });
};

export const useSenderInteractions = (params: {
  email: string;
  page?: string;
  limit?: string;
  isExport?: string;
  exportType?: string;
}) => {
  const { mailboxService } = useServices();

  return useQuery({
    queryKey: ["sender-interactions", params],
    queryFn: () => mailboxService.fetchSenderInteractions(params),
    enabled: !!params.email,
  });
};

// For export functionality
export const useExportSenderInteractions = () => {
  const { mailboxService } = useServices();

  return useMutation({
    mutationFn: (params: {
      email: string;
      isExport: string;
      exportType: string;
    }) => mailboxService.fetchSenderInteractions(params),
  });
};

export const useMailboxDailyStats = (params: {
  mailboxId: string;
  startDate: string;
  endDate: string;
  isMailboxStats: boolean;
  espType?: string;
}) => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["mailboxDailyStats", params],
    queryFn: () =>
      mailboxService.getMailboxDailyStats(
        params.mailboxId,
        params.startDate,
        params.endDate,
        params.isMailboxStats,
        params.espType
      ),
    enabled: !!params.mailboxId && !!params.startDate && !!params.endDate,
  });
};

export const useUpdateTargetAudience = () => {
  const { warmupService } = useServices();
  return useMutation({
    mutationFn: ({
      id,
      targetAudience,
    }: {
      id: string;
      targetAudience: TargetAudience;
    }) => warmupService.updateTargetAudience(id, targetAudience),
  });
};

// Seed counts hook
export const useSeedCounts = () => {
  const { mailboxService } = useServices();
  return useQuery({
    queryKey: ["seedCounts"],
    queryFn: () => mailboxService.getSeedCounts(),
  });
};

// Fetch sender IP hostnames hook
export const useFetchSenderIpHostnames = () => {
  const { mailboxService } = useServices();
  return useMutation({
    mutationFn: (data: {
      sender_email: string;
      esp_type: string;
      date: string;
      ips: string[];
    }) => mailboxService.fetchSenderIpHostnames(data),
  });
};
